
import { useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/branding-context';
import { useFeedbackConfig } from '@/hooks/useFeedbackConfig';
import { Modal } from '@/components/ui';
import { FeedbackForm } from './FeedbackForm';

const ASANA_ENV_URL = import.meta.env.VITE_ASANA_FEEDBACK_FORM_URL ?? '';
const ASANA_EMBED_STYLES = 'https://form.asana.com/static/asana-form-embed-style.css';

/** Ensure Asana form URL has embed=true so it loads correctly in iframe */
function asanaEmbedSrc(url: string): string {
  try {
    const u = new URL(url);
    if (!u.searchParams.has('embed')) u.searchParams.set('embed', 'true');
    return u.toString();
  } catch {
    return url.includes('?') ? `${url}&embed=true` : `${url}?embed=true`;
  }
}

export function FeedbackButton() {
  const { isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const { enabled: githubEnabled, isLoading: githubConfigLoading } = useFeedbackConfig();
  const pathname = useLocation().pathname;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const asanaUrl = (branding.feedbackFormUrl || ASANA_ENV_URL).trim();
  const useGithub = githubEnabled;
  const useAsana = !useGithub && !!asanaUrl;

  if (!isAuthenticated) return null;
  if (githubConfigLoading && !useAsana) return null;
  if (!useGithub && !useAsana) return null;
  // Keep the projection screen completely clean on the Live Display.
  if (pathname?.startsWith('/live-display')) return null;

  const embedSrc = useAsana ? asanaEmbedSrc(asanaUrl) : '';

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
        aria-label="Submit feedback or report a bug"
      >
        <FeedbackIcon className="h-6 w-6" />
      </button>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size={useGithub ? 'lg' : 'full'}
        showCloseButton={useGithub}
        panelClassName={
          useGithub
            ? undefined
            : 'bg-transparent shadow-none rounded-none'
        }
        contentClassName={useGithub ? undefined : '!p-0'}
      >
        {useGithub ? (
          <FeedbackForm onClose={() => setIsModalOpen(false)} />
        ) : (
          <div className="relative">
            <link rel="stylesheet" href={ASANA_EMBED_STYLES} />
            <div className="asana-embed-container">
              <iframe
                className="asana-embed-iframe"
                title="Bug report and feature request form"
                src={embedSrc}
                height={533}
                width={800}
                style={{ maxWidth: '100%', height: 'min(533px, 70vh)' }}
              />
              <div className="asana-embed-footer">
                <a
                  rel="nofollow noopener"
                  target="_blank"
                  className="asana-embed-footer-link"
                  href="https://asana.com/?utm_source=embedded_form"
                >
                  <span className="asana-embed-footer-text">Form powered by</span>
                  <div className="asana-embed-footer-logo" role="img" aria-label="Logo of Asana" />
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 text-slate-500 shadow-sm hover:bg-white hover:text-slate-700 dark:bg-slate-800/90 dark:hover:bg-slate-800 dark:hover:text-slate-300"
              aria-label="Close"
            >
              <CloseIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </Modal>
    </>
  );
}

function FeedbackIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
